/**
 * - `pending`: the service worker has not reported ready yet (first launch).
 * - `misconfigured`: no VAPID public key in this build, or the worker is
 *   disabled (dev / Vercel preview) -- an environment problem, not the phone's.
 * - `unsupported`: the browser lacks Notification / PushManager / SW APIs.
 */
export type PushClientStatus =
  | 'unsupported'
  | 'misconfigured'
  | 'pending'
  | 'needs-install'
  | 'denied'
  | 'off'
  | 'on';

export function isIosDevice(input: {
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}): boolean {
  if (/iPad|iPhone|iPod/.test(input.userAgent)) {
    return true;
  }
  return input.platform === 'MacIntel' && input.maxTouchPoints > 1;
}

export function detectPushClientGate(input: {
  hasNotification: boolean;
  hasPushManager: boolean;
  hasServiceWorker: boolean;
  standalone: boolean;
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
}): Extract<PushClientStatus, 'unsupported' | 'needs-install'> | 'ready' {
  if (
    !input.hasNotification ||
    !input.hasPushManager ||
    !input.hasServiceWorker
  ) {
    return 'unsupported';
  }
  if (isIosDevice(input) && !input.standalone) {
    return 'needs-install';
  }
  return 'ready';
}

export function permissionToToggleState(
  permission: NotificationPermission | 'unsupported',
  subscribed: boolean
): Extract<PushClientStatus, 'denied' | 'off' | 'on'> {
  if (permission === 'denied') {
    return 'denied';
  }
  return subscribed ? 'on' : 'off';
}

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}
